/**
 * Analytics Dashboard - Advanced usage analytics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Download, Radio, Users, Clock, Zap } from 'lucide-react';
import axios from 'axios';

const PRESETS = [
  { label: 'Hoy', days: 0 },
  { label: 'Semana', days: 7 },
  { label: 'Mes', days: 30 },
];

function toISODate(d) {
  return d.toISOString().split('T')[0];
}

function fmtDuration(secs) {
  if (!secs || isNaN(secs)) return '0s';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function StatCard({ icon: Icon, label, value, sub, color = '#007AFF' }) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 rounded-2xl flex-1 min-w-0"
      style={{ background: 'rgba(10,15,30,0.6)', border: `1px solid ${color}22` }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: color + '15' }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold text-n-800">{value}</div>
        <div className="text-xs text-n-500">{label}</div>
        {sub && <div className="text-xs mt-0.5" style={{ color }}>{sub}</div>}
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="px-3 py-2 rounded-xl text-xs"
        style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="text-n-700 font-semibold">{label}:00h</div>
        <div className="text-s-blue">{payload[0]?.value} transmisiones</div>
        {payload[1] && <div className="text-s-green">{fmtDuration(payload[1]?.value)}</div>}
      </div>
    );
  }
  return null;
};

export default function Analytics() {
  const today = toISODate(new Date());
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [preset, setPreset] = useState(0);
  const [overview, setOverview] = useState(null);
  const [byUser, setByUser] = useState([]);
  const [byChannel, setByChannel] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [loading, setLoading] = useState(true);

  const applyPreset = useCallback((idx) => {
    setPreset(idx);
    const end = new Date();
    const start = new Date();
    if (idx > 0) start.setDate(start.getDate() - PRESETS[idx].days);
    setDateFrom(toISODate(start));
    setDateTo(toISODate(end));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `dateFrom=${dateFrom}&dateTo=${dateTo}`;
      const [ov, users, channels, hr] = await Promise.all([
        axios.get(`/api/admin/analytics/overview?${params}`),
        axios.get(`/api/admin/analytics/by-user?${params}`),
        axios.get(`/api/admin/analytics/by-channel?${params}`),
        axios.get(`/api/admin/analytics/hourly?date=${dateFrom}`),
      ]);
      setOverview(ov.data);
      setByUser(users.data);
      setByChannel(channels.data);
      setHourly(hr.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    const url = `/api/admin/analytics/export?dateFrom=${dateFrom}&dateTo=${dateTo}&format=csv`;
    window.open(url, '_blank');
  };

  const maxCount = Math.max(...hourly.map(h => h.count), 1);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-n-800">Analytics</h1>
          <p className="text-sm text-n-500 mt-0.5">Análisis detallado de actividad PTT</p>
        </div>
        <button onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: 'rgba(52,199,89,0.12)', border: '1px solid rgba(52,199,89,0.25)', color: '#34C759' }}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      {/* Date range */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(0,0,0,0.06)' }}>
          {PRESETS.map((p, i) => (
            <button key={i} onClick={() => applyPreset(i)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${preset === i ? 'text-n-800' : 'text-n-500 hover:text-n-700'}`}
              style={preset === i ? { background: '#007AFF' } : {}}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPreset(-1); }}
            className="px-3 py-1.5 rounded-xl text-xs text-n-700 outline-none"
            style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }} />
          <span className="text-n-400 text-xs">→</span>
          <input type="date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPreset(-1); }}
            className="px-3 py-1.5 rounded-xl text-xs text-n-700 outline-none"
            style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }} />
        </div>
        <button onClick={load}
          className="px-4 py-1.5 rounded-xl text-xs font-semibold text-n-800"
          style={{ background: '#007AFF' }}>
          Aplicar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-n-500 text-sm">Cargando...</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Radio} label="Transmisiones" value={overview?.totalTransmissions ?? 0} color="#007AFF" />
            <StatCard icon={Clock} label="Minutos totales"
              value={fmtDuration(overview?.totalDuration ?? 0)} color="#34C759" />
            <StatCard icon={Users} label="Usuarios activos" value={overview?.activeUsers ?? 0} color="#C77DFF" />
            <StatCard icon={Zap} label="Canal más activo"
              value={overview?.topChannel?.name || '—'}
              sub={overview?.peakHour !== null ? `Hora pico: ${overview.peakHour}:00` : null}
              color="#FF9500" />
          </div>

          {/* Hourly chart */}
          <div className="p-5 rounded-2xl" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <h2 className="text-sm font-bold text-n-700 mb-4">Actividad por Hora — {dateFrom}</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourly} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <XAxis dataKey="hour" tick={{ fill: '#475569', fontSize: 10 }}
                  tickFormatter={h => `${h}h`} />
                <YAxis tick={{ fill: '#475569', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {hourly.map((entry, index) => (
                    <Cell key={index}
                      fill={entry.count === maxCount && maxCount > 0 ? '#FF9500' : '#007AFF'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top users & channels */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Top users */}
            <div className="p-5 rounded-2xl" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h2 className="text-sm font-bold text-n-700 mb-4">Top 5 Usuarios</h2>
              {byUser.length === 0 ? (
                <p className="text-xs text-n-400 text-center py-6">Sin datos</p>
              ) : (
                <div className="space-y-2">
                  {byUser.slice(0, 5).map((u, i) => (
                    <div key={u.userId} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: (u.avatarColor || '#007AFF') + '22', color: u.avatarColor || '#007AFF' }}>
                        {(u.username || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-n-800 truncate">{u.username}</div>
                        <div className="h-1.5 rounded-full mt-1 bg-slate-800 overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${(u.transmissionCount / (byUser[0]?.transmissionCount || 1)) * 100}%`, background: u.avatarColor || '#007AFF' }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-bold text-n-800">{u.transmissionCount}</div>
                        <div className="text-xs text-n-500">{fmtDuration(u.totalDuration)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top channels */}
            <div className="p-5 rounded-2xl" style={{ background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h2 className="text-sm font-bold text-n-700 mb-4">Top 5 Canales</h2>
              {byChannel.length === 0 ? (
                <p className="text-xs text-n-400 text-center py-6">Sin datos</p>
              ) : (
                <div className="space-y-2">
                  {byChannel.slice(0, 5).map((ch, i) => (
                    <div key={ch.channelId} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: 'rgba(0,0,0,0.06)', color: '#007AFF' }}>
                        #
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-n-800 truncate">{ch.name}</div>
                        <div className="h-1.5 rounded-full mt-1 bg-slate-800 overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${(ch.transmissionCount / (byChannel[0]?.transmissionCount || 1)) * 100}%`, background: '#007AFF' }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-bold text-n-800">{ch.transmissionCount}</div>
                        <div className="text-xs text-n-500">{ch.uniqueUsers} usuarios</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
